import { untrack, type JSX } from 'solid-js'
import type { SetStoreFunction } from 'solid-js/store'
import { cloneBox, type Box, type Position, type Dimensions } from '../../types'
export type { Dimensions }
import type { ObjectContext } from '../../project/objects'
import type { HistoryContext } from '../../project/history'
import { clampAxisIntoParent } from './dragBounds'
import type { ITextImpl } from '../text/AbstractTextImpl'
import type { ImageImpl } from '../images/ImageImpl'
import type { ContainerImpl } from '../container/ContainerImpl'
import type { SelectionContext } from '../selection'
import type { DragContext } from '../drag'
import type { Mode } from '../../project/PosterMode'
import type { TextResizeMode } from './ResizeMode'

export type ObjectType = 'text' | 'image' | 'container';
export type SetPosFn     = (v: Box) => void
export type	ResizeHandles = 'box' | 'fieldBox' | 'textCorner' | 'none';


// The result of measuring an object's content to fit: the hugged box (box.dims is the minimum/hug
// size, box.pos the position it should snap to — a fit with no opinion on position carries the object's
// current pos, so applying it is a no-op there), the content baseline offset (dy), and whether the DOM
// measurement had actually laid out yet (measured — the fit-retry gate).
export type HugFit       = { box: Box; dy?: number; measured?: boolean }


// The object's resolved render geometry as a Box: position resolved through any container layout
// override, and box height. dims.width is a dummy 0 here — getBBox/getHitBBox fill it from pos.width.

// The selection-outline geometry a host draws around an object. Most objects draw a closed dashed
// rectangle (open:false). A design-mode template field draws an "open" box instead: side ticks sideH
// tall starting at sideTop, plus a single horizontal line on the bounded (aligned) edge — hLineTop for
// a top-aligned field, hLineBottom for a bottom-aligned one, neither when centred.
export type SelectionBox = { open: boolean; sideTop: number; sideH: number; hLineTop: boolean; hLineBottom: boolean }

// The initial geometry the factory seeds onto an impl: the starting box — its stored position and
// size (text derives the size from boxWidth()/fieldHeight()) — and the optional container layout
// override. layoutPos is the flex-layout override a parent container supplies: the position it returns
// replaces the object's own stored one for rendering, bbox and selection; undefined → normal absolute.
export type GeometrySeed = {
	box: Box
	layoutPos?: () => Position | undefined
}

// The box a factory falls back to for whichever of position/size the object doesn't carry itself.
// A seed built from it *aliases* these objects — as it aliases the runtime object's own position/size
// when they are present — so anything that keeps a seed box past the call must cloneBox() it first.
// That is what seeds the live-geometry stores: createStore(cloneBox(seed().box)).
export const defaultSeedBox: Box = { pos: { x: 0, y: 0 }, dims: { width: 200, height: 60 } }


// The live registry of mounted object impls, keyed by object id. Each SelectableObject registers
// its impl here on setup and removes it on cleanup; geometry/command code looks impls up by id to
// read live bboxes or drive writeGeometry/refreshSize. Owned by the selection state (see selection.ts).
export type ImplRegistry = Record<string, IObjectImpl>

// The common host surface every object impl provides. Type-specific state (a container's
// fill/border, an image's zoom/src, text content/alignment) lives on the concrete impls
// (ContainerImpl/ImageImpl/ITextImpl); clients reach it by casting the impl.
export interface IObjectImpl
{
	readonly id: string;

	type(): ObjectType;
	isField(): boolean;
	resizeHandles(): ResizeHandles;
	lockAspect(): boolean;
	getHugFit(): HugFit | undefined;
	fieldHeight(): number | undefined;

	// ── Live geometry ───────────────────────────────────────────────────────────
	// The store is injected by the factory through the constructor; see ObjectImpl. box is the live
	// geometry Box — a read-only reactive view. The raw store setter is private to ObjectImpl;
	// every write goes through writeGeometry, the single chokepoint (text gates it on font readiness).
	// writePos/writeDims are the single-sub-box writes, both routed through it.
	// effectiveBox resolves box.pos through any container layout override.
	readonly box: Box;
	writeGeometry(box: Box): void;
	writePos(pos: Position): void;
	writeDims(dims: Dimensions): void;
	effectiveBox(userExists:boolean): Box;
	contentOffsetY(): number;
	// True when a parent container currently supplies this object's position (a flex layoutPos): its
	// left/top are owned by the parent, so the host turns a drag-move into a sibling reorder instead.
	isLayoutManaged(): boolean;

	// ── Registry-facing geometry surface (formerly the createElement wrapper) ─────
	getBBox(userExists:boolean): Box;
	getHitBBox(userExists:boolean): Box;
	// The top coordinate to persist for this object, given its live getBBox().y. Defaults to that
	// bbox top, but a user-mode field anchors its hugged box at designTop + the baseline offset (see
	// TemplateTextImpl getMinSize), so it reports designTop here — otherwise export would bake the
	// offset into position.top and the next reload would re-add it, drifting every field down a line.
	storedTop(bboxTop: number): number;
	// As storedTop, for the other two axes a user-mode field's live box hugs its content rather than
	// keeping its designed geometry: the hugged box is anchored inside the design width per textAlign,
	// so its left drifts inward and its width is the ink width, not the wrap width.
	storedLeft(bboxLeft: number): number;
	storedWidth(bboxWidth: number): number | undefined;
	// Convert between a stored design position and the live rendered box. They are identity for every
	// object whose live box is its stored box; a user-mode field's box hugs and is anchored inside its
	// design box, so it overrides both to strip (storedPositionOf) / re-add (livePosition) the hug insets.
	// A move persists storedPositionOf(live) and the move command re-renders via livePosition(stored).
	// storedPositionOf converts a live ink position to the persisted design position; livePosition is its
	// inverse, re-anchoring a persisted design position back to the live rendered position.
	storedPositionOf(ink: Position): Position;
	livePosition(design: Position): Position;
	getSize(): Dimensions;
	// The size the *template layout* reserves for this object (designLayout's metricsOf input), as
	// against getSize(), the size it draws at. They differ only for a user-mode field: it is laid out
	// in its design width — a container has to align the field's reserved box, not its glyph run — but
	// it draws shrink-wrapped around its ink. displayBox() converts a placed layout box back.
	layoutSize(): Dimensions;
	// The box the object draws/selects at, given the box the template layout placed it in. Identity for
	// everything whose layout box is its drawn box; a hugging field returns its ink box, inset inside
	// the placed design width per textAlign. The inverse direction is storedPositionOf/livePosition,
	// which converts the drawn box to the design box the store holds.
	displayBox(laidOut: Box): Box;
	getContentOffsetY(): number;
	getEl(): SVGElement | null;
	getTextEl(): SVGTextElement | null;
	refreshSize(clampTop?: boolean): void;
	// The command contexts are passed in (not held) so the text impls can record the post-edit
	// re-fit as a history step; non-text impls ignore them.
	refreshContent(objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext): void;

	// ── Host interaction seam ─────────────────────────────────────────────────────
	// Type-specific behaviour the SelectableObject host used to branch on with type()=='text'/'image'.
	// Each has an inert default on ObjectImpl; text/image override the ones they need, so the host drives
	// them polymorphically. setupGeometry runs the initial text fit; onResizeEnd commits a text resize;
	// cursorOverride/onShiftDrag/isMovable carry image behaviour; handlesDoubleClick gates same-element
	// double-click detection; handleDoubleClick is the object's double-click action (text opens the
	// editor, image runs upload/rebind); selectionBox supplies the selection-outline geometry.
	setupGeometry(): void;
	onResizeEnd(mode: TextResizeMode, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext): void;
	cursorOverride(selCtx: SelectionContext): string | undefined;
	isMovable(): boolean;
	onShiftDrag(e: MouseEvent, dragCtx: DragContext, histCtx: HistoryContext): void;
	handlesDoubleClick(): boolean;
	handleDoubleClick(e: MouseEvent, selCtx: SelectionContext, mode: Mode): void;
	selectionBox(h: number): SelectionBox;
	// The rendered selection outline drawn inside the handles-object group. The default is the closed
	// dashed rectangle; a design-mode template field overrides it with the "open" tick-and-line box
	// (see TemplateTextImpl). Returns JSX so the impl owns the drawing, keeping the host type-agnostic.
	selectionComponent(): JSX.Element;
}

// The concrete object impls a SelectableObject / ResizeHandles host can drive. The host reads the
// shared IObjectImpl surface; the union lets a call site narrow to
// reach the type-specific methods (text fit/resize callbacks, an image's crop/upload) that don't
// belong on the common interface — replacing the former `as unknown as ITextImpl` casts and the
// optional-chained `impl.foo?.()` no-ops.
export type DraggableImpl = ITextImpl | ImageImpl | ContainerImpl

// Push a design-store geometry change into the live geometry store, so an object whose stored
// position/size is replaced under it — a whole new design loaded in place, an undo, a command —
// follows it without needing a remount to re-seed.
//
// Called from each object's impl factory memo, which already re-runs whenever the design changes.
// designBox is the object's geometry as the *design* carries it (its stored position/size with the
// type's constant fallbacks folded in, and no measurement); the diff is against the box last adopted
// from there, never against the live box, so nothing the live box owns alone is clobbered — a measured
// text height, an in-flight drag and a hug-fitted field all survive, because only what actually moved
// in the design is written. What gets written is the seed, not designBox, so a design-mode field's
// reserved height is re-derived; position goes through livePosition() (the inverse of the
// storedPositionOf() a move persists) so a hugging user-mode field re-anchors rather than jumping by
// its hug inset.
//
// Untracked, as with seedEditable in objects.ts: the caller is a memo, and this write must not make
// it self-referential.
// One axis of a live-geometry write: the incoming value when it is a usable number, else the value the
// box already holds. Both unusable only if the seed itself was poisoned (the load pass repairs those —
// see migrateGeometry), and 0 is the one answer that still renders.
function finiteAxis(next: number, prev: number): number
{
	if (Number.isFinite(next)) return next
	return Number.isFinite(prev) ? prev : 0
}

// `box` with every non-finite component replaced by the one `keep` already holds (see writeGeometry).
function finiteBox(box: Box, keep: Box): Box
{
	return {
		pos:  { x:     finiteAxis(box.pos.x,      keep.pos.x),      y:      finiteAxis(box.pos.y,       keep.pos.y) },
		dims: { width: finiteAxis(box.dims.width, keep.dims.width), height: finiteAxis(box.dims.height, keep.dims.height) },
	}
}

export function adoptDesignGeometry(impl: IObjectImpl, designBox: Box, seed: GeometrySeed, prev: { current: Box | null }): void
{
	untrack(() => {
		const last = prev.current
		prev.current = cloneBox(designBox)
		if (!last) return   // first call: the live store was just seeded from this very geometry

		if (last.pos.x !== designBox.pos.x || last.pos.y !== designBox.pos.y)
			impl.writePos(impl.livePosition(seed.box.pos))

		if (last.dims.width !== designBox.dims.width || last.dims.height !== designBox.dims.height)
			impl.writeDims(seed.box.dims)
	})
}

export abstract class ObjectImpl
{
//XXX abstract? or make it id()?
	abstract readonly id: string;

	// Live geometry — the store is built by the caller (via createStore(cloneBox(seed().box))) and
	// injected through the constructor. pos is the read-only reactive view; the raw store setter is
	// kept private so every write goes through writeGeometry (the single chokepoint AbstractTextImpl
	// gates on font readiness). layoutPos is the optional container flex-layout override applied in
	// effectiveBox().
	readonly box: Box;
	readonly #setBoxRaw: SetStoreFunction<Box>;
	protected readonly layoutPos?: () => Position | undefined;

	// Text content can render above box.pos.y (negative offset); image/container never do, so the
	// base is a constant 0 that AbstractTextImpl replaces with its real signal in its constructor.
	contentOffsetY: () => number = () => 0;

	lockAspect(): boolean { return false }

	constructor(box: Box, setBox: SetStoreFunction<Box>, seed: GeometrySeed)
	{
		this.box        = box
		this.#setBoxRaw = setBox
		this.layoutPos  = seed.layoutPos
	}

	// The single write path for live geometry. Public so the drag/resize handlers go through it rather
	// than touch the store directly, and overridable so text can gate writes on font readiness (see
	// AbstractTextImpl.writeGeometry). The base writes immediately — image/container geometry doesn't
	// depend on font metrics.
	//
	// Non-finite components are refused per axis, keeping the value the box already holds. This is the
	// only door into the live store, and the store is what AutoSave persists — so a measurement that
	// went NaN (a style value that isn't a number, a division by an unmeasured box) can neither reach
	// the screen as an invalid transform nor be written into the design document, where it would
	// outlive the bug that produced it and defeat every `??` fallback that reads it back.
	writeGeometry(box: Box): void
	{
		this.#setBoxRaw(finiteBox(box, this.box))
	}

	// Write one sub-box, carrying the other one over unchanged. The store unwraps the carried sub-box
	// to the object it already holds, so its subscribers aren't notified.
	writePos(pos: Position): void { this.writeGeometry({ pos, dims: this.box.dims }) }
	writeDims(dims: Dimensions): void { this.writeGeometry({ pos: this.box.pos, dims }) }

	// The object's resolved render geometry: stored left/top resolved through any container layout
	// override, as a Box position, with box height. dims.width is a dummy 0 — getBBox/getHitBBox fill
	// it from pos.width. A plain method (recomputed per read); clients wrap it in a memo if needed.
	effectiveBox(_userExists:boolean): Box
	{
		const pos = this.layoutPos?.() ?? this.box.pos
		const h   = this.box.dims.height
		return { pos: { x: pos.x, y: pos.y }, dims: { width: 0, height: h } }
	}

	isLayoutManaged(): boolean { return !!this.layoutPos?.() }

	// User-mode "pull the box back inside its parent" tail shared by the text and image effectiveBox
	// overrides: clamp the resolved origin so the box sits inside [0, canvas] on each axis, folding in
	// contentOffsetY (0 for non-text) on the vertical axis. Callers keep their own gate (full-bleed /
	// layoutPos / !userExists) and pass their canvas dims; ContainerImpl clamps its own grown box.
	protected clampBoxIntoParent(base: Box, canvasW: number | undefined, canvasH: number | undefined): Box
	{
		const w = this.getSize().width
		const x = clampAxisIntoParent(base.pos.x, w,               canvasW ?? w)
		const y = clampAxisIntoParent(base.pos.y, base.dims.height, canvasH ?? base.dims.height, this.contentOffsetY())
		return { pos: { x, y }, dims: base.dims }
	}

	getBBox(userExists:boolean): Box {
		const eb = this.effectiveBox(userExists)
		return { pos: eb.pos, dims: { width: this.box.dims.width, height: eb.dims.height } };
	}

	// Size only, reading no position — lets a parent container measure this child for flex
	// layout without forming a reactive cycle through layoutPos/getBBox.
	getSize(): Dimensions { return { width: this.box.dims.width, height: this.box.dims.height } }

	// Laid out at, and drawn at, the same box: only a hugging user-mode field pulls these apart
	// (PlainWrap.layoutSize/displayBox).
	layoutSize(): Dimensions { return this.getSize() }
	displayBox(laidOut: Box): Box { return laidOut }

	getContentOffsetY(): number { return this.contentOffsetY() }

	// Hit-test bbox accounts for contentOffsetY: text content can render above pos.top when
	// contentOffsetY is negative, so we shift the y origin to match.
	getHitBBox(userExists:boolean): Box {
		const eb = this.effectiveBox(userExists)
		return { pos: { x: eb.pos.x, y: eb.pos.y + this.contentOffsetY() }, dims: { width: this.box.dims.width, height: eb.dims.height } }
	}

	// Default: persist the live box. The user-mode field facade overrides all three to report its
	// designed geometry instead of its hugged one (see ObjectImpl.storedTop in the interface).
	storedTop(bboxTop: number): number { return bboxTop }
	storedLeft(bboxLeft: number): number { return bboxLeft }
	storedWidth(bboxWidth: number): number | undefined { return bboxWidth }

	// Identity by default: the live box is the stored box. Only a hugging user-mode field overrides these.
	storedPositionOf(ink: Position): Position { return { x: ink.x, y: ink.y } }
	livePosition(design: Position): Position { return { x: design.x, y: design.y } }

	// DOM refs and content fit are overridden where relevant (ContainerImpl.getEl,
	// AbstractTextImpl.getTextEl/refreshSize/refreshContent); the base is inert for the rest.
	getEl(): SVGElement | null { return null }
	getTextEl(): SVGTextElement | null { return null }
	refreshSize(_clampTop?: boolean): void {}
	refreshContent(_objCtx?: ObjectContext, _impls?: ImplRegistry, _histCtx?: HistoryContext): void {}

	// ── Host interaction seam ───────────────────────────────────────────────────
	// Inert defaults; AbstractTextImpl/ImageImpl override the ones that carry type-specific behaviour.
	setupGeometry(): void {}
	onResizeEnd(_mode: TextResizeMode, _objCtx: ObjectContext, _impls: ImplRegistry, _histCtx: HistoryContext): void {}
	cursorOverride(_selCtx: SelectionContext): string | undefined { return undefined }
	isMovable(): boolean { return true }
	onShiftDrag(_e: MouseEvent, _dragCtx: DragContext, _histCtx: HistoryContext): void {}
	handlesDoubleClick(): boolean { return false }
	handleDoubleClick(_e: MouseEvent, _selCtx: SelectionContext, _mode: Mode): void {}
	selectionBox(_h: number): SelectionBox { return { open: false, sideTop: 0, sideH: 0, hLineTop: false, hLineBottom: false } }

	// The closed dashed rectangle every object but a design-mode template field draws. Reads its size
	// reactively through getSize() so the outline tracks the live box; TemplateTextImpl overrides it.
	selectionComponent(): JSX.Element
	{
		return (
			<rect
				x="0" y="0"
				width={this.getSize().width} height={this.getSize().height}
				fill="none"
				stroke="#4d9eff"
				stroke-width="1.5"
				stroke-dasharray="4 3"
			/>
		)
	}
}
